import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import { API } from "./shared";

const LABELS = { bids: "Bids", files: "Uploaded files & KYC documents", notifications: "Notifications", job_views: "Job views", own_sub_rfqs: "Own sub-RFQs",
  material_bids: "Sub-RFQ bids", artwork_preview_log: "Artwork preview log", guide_views: "Guide views" };

export function SupplierDeleteBox({ uid, close, onDeleted }) {
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/suppliers/${uid}/delete-check`).then(r => setInfo(r.data)).catch(e => { toast.error(e.response?.data?.detail || "Could not check"); close(); }); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!info) return <div style={{ padding: 14, color: "#64748b" }}>Checking…</div>;
  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/admin/suppliers/${uid}`, { data: { reason: reason.trim(), confirm } });
      toast.success(`Supplier deleted${r.data.email_sent ? " · confirmation email sent" : " · email could not be sent"}`);
      onDeleted();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.blockers) setInfo({ ...info, blockers: d.blockers });
      toast.error(d?.message || d || "Delete failed");
    } finally { setBusy(false); }
  };
  const blocked = info.blockers.length > 0 || info.is_test_account;
  return (
    <div data-testid="so-delete-box" style={{ border: "1px solid #FCA5A5", background: "#FEF2F2", borderRadius: 12, padding: 16, margin: "0 0 16px" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800, color: "#991B1B" }}><AlertTriangle size={16}/>Delete {info.name} permanently</div>
      {info.is_test_account && <p data-testid="so-delete-protected" style={{ fontSize: 13, color: "#991B1B" }}>This is a protected demo account and can't be deleted.</p>}
      {info.blockers.length > 0 ? (
        <div data-testid="so-delete-blockers" style={{ marginTop: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Finish or reassign this active work first:</div>
          <ul style={{ fontSize: 12.5, margin: "6px 0 0 18px" }}>{info.blockers.map(b => <li key={`${b.type}-${b.id}`}>{b.label}</li>)}</ul>
        </div>
      ) : !info.is_test_account && (
        <>
          <div style={{ fontSize: 12.5, color: "#7F1D1D", margin: "6px 0 10px" }}>
            This erases the login, profile, KYC documents, files, bids and messages. It can't be undone.
            Tax invoices and payment records ({info.will_delete.tax_records_kept}) are kept with the name replaced by "Deleted supplier", as GST law requires.
            A confirmation email goes to {info.email}.
          </div>
          <div data-testid="so-delete-counts" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {Object.entries(LABELS).map(([k, l]) => <span key={k} style={{ fontSize: 11.5, background: "#fff", border: "1px solid #FECACA", borderRadius: 999, padding: "2px 9px" }}>{l}: {info.will_delete[k] ?? 0}</span>)}
          </div>
          <textarea data-testid="so-delete-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (kept in the admin audit log)"
            style={{ width: "100%", border: "1px solid #FCA5A5", borderRadius: 8, padding: "7px 9px", fontSize: 13, fontFamily: "inherit" }}/>
          <input data-testid="so-delete-confirm" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={`Type "${info.name}" to confirm`}
            style={{ width: "100%", border: "1px solid #FCA5A5", borderRadius: 8, padding: "7px 9px", fontSize: 13, marginTop: 8 }}/>
        </>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        {!blocked && <button data-testid="so-delete-submit" className="so-btn so-red" style={{ background: "#B91C1C", color: "#fff", borderColor: "#B91C1C" }}
          disabled={busy || reason.trim().length < 3 || confirm.trim().toLowerCase() !== (info.name || "").trim().toLowerCase()} onClick={run}><Trash2 size={14}/>{busy ? "Deleting…" : "Delete permanently"}</button>}
        <button data-testid="so-delete-cancel" className="so-btn" onClick={close}>Cancel</button>
      </div>
    </div>
  );
}
